import { Head, router, useForm } from '@inertiajs/react'
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  CloudUpload,
  HelpCircle,
  Loader2,
  XCircle,
} from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import { PageHeader } from '@/components/PageHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { backupsPath } from '@/lib/routes'

type BackupStatus = 'running' | 'succeeded' | 'failed'

type BackupRow = {
  id: number
  status: BackupStatus
  started_at: string
  finished_at: string | null
  duration_display: string | null
  size_display: string | null
  snapshot_short: string | null
  error_message: string | null
}

type Health =
  | { status: 'never_run' }
  | { status: 'in_progress'; started_at: string; elapsed_display: string }
  | { status: 'stuck'; started_at: string; elapsed_display: string }
  | { status: 'healthy'; finished_at: string }
  | { status: 'stale'; finished_at: string }
  | { status: 'failed'; finished_at: string | null; error_message: string | null }

type Props = {
  backup_runs: BackupRow[]
  health: Health
}

const POLL_INTERVAL_MS = 5000
const TICK_INTERVAL_MS = 15000
const RELOAD_KEYS = ['backup_runs', 'health']

function relativeTime(iso: string | null): string {
  if (!iso) return '—'
  const diff = Math.round((Date.now() - new Date(iso).getTime()) / 1000)
  if (diff < 60) return 'just now'
  const minutes = Math.round(diff / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

function HealthBanner({ health }: { health: Health }) {
  const shape = bannerShape(health)
  return (
    <div
      className={`mb-6 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${shape.className}`}
    >
      <shape.Icon className={`mt-0.5 h-5 w-5 shrink-0 ${shape.iconClass}`} />
      <div className="flex-1">
        <p className="font-medium">{shape.title}</p>
        {shape.detail && <p className="mt-0.5 text-xs opacity-80">{shape.detail}</p>}
      </div>
    </div>
  )
}

function bannerShape(h: Health): {
  Icon: typeof CheckCircle2
  iconClass: string
  className: string
  title: string
  detail?: ReactNode
} {
  switch (h.status) {
    case 'in_progress':
      return {
        Icon: Loader2,
        iconClass: 'animate-spin text-amber-600',
        className: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100',
        title: `Backup in progress — running for ${h.elapsed_display}`,
        detail: `Started ${relativeTime(h.started_at)}`,
      }
    case 'stuck':
      return {
        Icon: AlertTriangle,
        iconClass: 'text-red-600',
        className: 'border-red-200 bg-red-50 text-red-900 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-100',
        title: `Stuck — no completion reported in ${h.elapsed_display}`,
        detail: `Started ${relativeTime(h.started_at)}. The worker likely crashed; it will be auto-reaped within the hour.`,
      }
    case 'healthy':
      return {
        Icon: CheckCircle2,
        iconClass: 'text-emerald-600',
        className: 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-100',
        title: `Healthy — last backup succeeded ${relativeTime(h.finished_at)}`,
      }
    case 'stale':
      return {
        Icon: Clock,
        iconClass: 'text-amber-600',
        className: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100',
        title: `Stale — last backup was ${relativeTime(h.finished_at)}`,
        detail: 'Expected a daily backup. Check the jobs worker.',
      }
    case 'failed':
      return {
        Icon: XCircle,
        iconClass: 'text-red-600',
        className: 'border-red-200 bg-red-50 text-red-900 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-100',
        title: `Last backup failed ${relativeTime(h.finished_at)}`,
        detail: h.error_message || undefined,
      }
    case 'never_run':
    default:
      return {
        Icon: HelpCircle,
        iconClass: 'text-muted-foreground',
        className: 'border-border bg-muted/30 text-foreground',
        title: 'No backups yet',
        detail: 'The next scheduled run is 3 AM daily.',
      }
  }
}

function StatusBadge({ status }: { status: BackupStatus }) {
  if (status === 'running') {
    return (
      <Badge variant="secondary" className="gap-1 bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
        <Loader2 className="h-3 w-3 animate-spin" />
        running
      </Badge>
    )
  }
  if (status === 'failed') {
    return <Badge variant="destructive">failed</Badge>
  }
  return (
    <Badge variant="secondary" className="bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100">
      succeeded
    </Badge>
  )
}

function BackupRunsIndex({ backup_runs, health }: Props) {
  const runInFlight = health.status === 'in_progress'
  const { post, processing } = useForm({})
  const [, setTick] = useState(0)

  // Poll while a backup is running so the page reflects progress without a manual refresh.
  useEffect(() => {
    if (!runInFlight) return
    const handle = window.setInterval(() => {
      router.reload({ only: RELOAD_KEYS })
    }, POLL_INTERVAL_MS)
    return () => window.clearInterval(handle)
  }, [runInFlight])

  // Force a re-render every 15s so relativeTime() labels ("just now", "2m ago")
  // stay fresh without a server roundtrip.
  useEffect(() => {
    const handle = window.setInterval(() => setTick((t) => t + 1), TICK_INTERVAL_MS)
    return () => window.clearInterval(handle)
  }, [])

  const triggerBackup = () => {
    if (runInFlight || processing) return
    post(backupsPath(), { preserveScroll: true })
  }

  const triggerDisabled = runInFlight || processing
  const triggerLabel = useMemo(() => {
    if (runInFlight) return 'In progress…'
    if (processing) return 'Enqueuing…'
    return 'Run backup now'
  }, [runInFlight, processing])

  return (
    <>
      <Head title="Backups" />

      <div className="mx-auto max-w-5xl px-8 py-10">
        <PageHeader
          title="Backups"
          description="Daily off-site backups of the database and uploaded files, encrypted and stored in Google Drive."
          actions={
            <Button onClick={triggerBackup} disabled={triggerDisabled}>
              {runInFlight ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CloudUpload className="h-4 w-4" />
              )}
              {triggerLabel}
            </Button>
          }
        />

        <HealthBanner health={health} />

        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-6 py-3 font-medium">Started</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Duration</th>
                  <th className="px-6 py-3 font-medium">Size</th>
                  <th className="px-6 py-3 font-medium">Snapshot</th>
                  <th className="px-6 py-3 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {backup_runs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-10 text-center text-muted-foreground">
                      No backups have run yet.
                    </td>
                  </tr>
                )}
                {backup_runs.map((r) => (
                  <tr key={r.id}>
                    <td className="px-6 py-4" title={r.started_at}>
                      {relativeTime(r.started_at)}
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-6 py-4 tabular-nums text-muted-foreground">
                      {r.duration_display ?? '—'}
                    </td>
                    <td className="px-6 py-4 tabular-nums text-muted-foreground">
                      {r.size_display ?? '—'}
                    </td>
                    <td className="px-6 py-4 font-mono text-xs text-muted-foreground">
                      {r.snapshot_short ?? '—'}
                    </td>
                    <td
                      className="max-w-md truncate px-6 py-4 text-xs text-muted-foreground"
                      title={r.error_message ?? undefined}
                    >
                      {r.error_message ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

export default Object.assign(BackupRunsIndex, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
